const instances: Map<string, BrowserStorageService<any>> = new Map();

type K = string;

export interface StorageServiceOptions {
  storage?: Storage
}

export class BrowserStorageService<V = object | number | boolean | string | null | undefined> {
  public static clearAll() {
    instances.forEach((s) => s.clear());
  }

  public static destroyAll() {
    instances.forEach((s) => s.destroy());
  }

  private storage: Storage | null;

  private state: Map<string, V> | null;

  public constructor(public readonly name: string, options?: StorageServiceOptions) {
    this.storage = options?.storage ?? globalThis.localStorage;

    if (instances.get(name)) {
      throw new Error(`Instance of '${name}' already exists`);
    }

    try {
      const persistedState = this.storage.getItem(this.storageKey) || "[]";
      this.state = new Map(JSON.parse(persistedState));
    } catch {
      this.state = new Map();
    }
    instances.set(name, this);
  }

  private get storageKey() {
    return `StorageService[${this.name}]`;
  }

  private persist() {
    const entries = Array.from(this.state!.entries());
    this.storage!.setItem(this.storageKey, JSON.stringify(entries));
  }

  public get length(): number {
    return this.state!.size;
  }

  public clear(): void {
    this.state!.clear();
    this.persist();
  }

  public getItem(key: K = "default"): V | null {
    return this.state!.get(String(key)) ?? null;
  }

  public removeItem(key: K = "default"): void {
    this.state!.delete(String(key));
    this.persist();
  }

  public setItem(value: V, key = "default"): void {
    this.state!.set(String(key), value);
    this.persist();
  }

  public destroy(): void {
    instances.delete(this.name);
    this.state = null;
    this.storage = null;
  }
}
